"use client";

import {
  searchAutocomplete,
  type AutocompleteGroup,
  type AutocompleteHit,
} from "@/actions/search-autocomplete";
import InputField from "@/components/form/input-field";
import SearchTabsClient from "@/components/search-tabs/client";
import type { SearchTab } from "@/components/search-tabs";
import { createImageUrl } from "@/utils/image";
import classNames from "classnames";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import Icon from "../icon";

const ALL_TAB = "all";

const NavSearch = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [groups, setGroups] = useState<AutocompleteGroup[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [activeTab, setActiveTab] = useState<string>(ALL_TAB);
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const visibleGroups = useMemo(
    () =>
      activeTab === ALL_TAB
        ? groups
        : groups.filter((g) => g.index === activeTab),
    [groups, activeTab],
  );

  const tabs: SearchTab[] = useMemo(() => {
    if (groups.length === 0) return [];
    const total = groups.reduce((sum, g) => sum + g.hits.length, 0);
    return [
      { key: ALL_TAB, label: "Allt", count: total },
      ...groups.map((g) => ({
        key: g.index,
        label: g.label,
        count: g.hits.length,
      })),
    ];
  }, [groups]);

  // Flat list of all hits for keyboard navigation (only visible groups)
  const allHits: AutocompleteHit[] = visibleGroups.flatMap((g) => g.hits);

  const handleOpen = () => {
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchInput("");
    setGroups([]);
    setShowSuggestions(false);
    setActiveTab(ALL_TAB);
  };

  const handleSelectTab = (key: string) => {
    setActiveTab(key);
    setActiveIndex(-1);
  };

  const handleSubmit = () => {
    if (searchInput.trim()) {
      router.push(`/sok?q=${encodeURIComponent(searchInput.trim())}`);
      handleClose();
    }
  };

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!searchInput || searchInput.trim().length < 2) {
      setGroups([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const result = await searchAutocomplete(searchInput);
      setGroups(result);
      setShowSuggestions(result.length > 0);
      setActiveIndex(-1);
      setActiveTab(ALL_TAB);
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || allHits.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, allHits.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      if (activeIndex >= 0 && allHits[activeIndex]) {
        router.push(allHits[activeIndex].url);
        handleClose();
      } else {
        handleSubmit();
      }
    }
    if (e.key === "Escape") handleClose();
  };

  if (isOpen) {
    return (
      <div className="absolute inset-0 z-20 bg-sand-600 flex items-center pl-4 pr-0">
        <div className="relative flex-1 h-[50px]">
          <InputField
            label="Sök"
            type="text"
            placeholder="Sök efter innehåll"
            containerClassName="h-[50px] absolute right-0 w-full"
            className="h-full rounded-none pr-[60px] border-x-0 border-t-0"
            onChange={(e: any) => {
              setSearchInput(e.target.value);
              setShowSuggestions(true);
            }}
            onKeyDown={(e: any) => handleKeyDown(e)}
            onKeyUpCapture={(e: any) => handleKeyUp(e)}
            value={searchInput}
            name="search"
            autoComplete="off"
            inputRef={inputRef}
          />
          <button
            onClick={handleSubmit}
            aria-label="Utför sökning"
            className="btn btn-primary h-[39px] flex items-center absolute right-2 top-[5px] z-10 font-bold"
          >
            Sök
          </button>

          {showSuggestions && groups.length > 0 && (
            <div className="absolute top-full left-0 right-0 z-50 bg-sand-200 shadow-lg mt-0 rounded-sm overflow-y-auto max-h-[75vh]">
              {tabs.length > 1 && (
                <div className="border-b border-sand-500 sticky top-0 bg-sand-200 z-10">
                  <SearchTabsClient
                    tabs={tabs}
                    activeKey={activeTab}
                    onSelect={handleSelectTab}
                  />
                </div>
              )}
              {visibleGroups.map((group) => {
                let hitOffset = 0;
                for (const g of visibleGroups) {
                  if (g.index === group.index) break;
                  hitOffset += g.hits.length;
                }
                return (
                  <div key={group.index}>
                    {activeTab === ALL_TAB && (
                      <div className="flex items-center justify-between px-4 pt-3 pb-1">
                        <span className="text-xs uppercase font-bold text-gray-400 tracking-wider">
                          {group.label}
                        </span>
                        {group.hasMore && (
                          <a
                            href={group.showAllUrl}
                            className="text-sm text-gray-400 hover:underline"
                            onClick={handleClose}
                          >
                            Visa alla →
                          </a>
                        )}
                      </div>
                    )}
                    {group.hits.map((hit, i) => {
                      const flatIndex = hitOffset + i;
                      return (
                        <a
                          key={hit.url}
                          href={hit.url}
                          className={classNames(
                            "flex items-center gap-3 px-8 py-4 hover:bg-sand-800 cursor-pointer",
                            flatIndex === activeIndex && "",
                          )}
                          onMouseEnter={() => setActiveIndex(flatIndex)}
                          onClick={handleClose}
                        >
                          {hit.image &&
                            (hit.index === "authors" ? (
                              <div className="shrink-0 w-10 h-10 rounded-full overflow-hidden border border-static-sand-800 bg-static-sand-700">
                                <Image
                                  src={createImageUrl(hit.image, {
                                    crop: "1:1",
                                  })}
                                  alt=""
                                  width={40}
                                  height={40}
                                  className="object-cover w-full h-full"
                                />
                              </div>
                            ) : (
                              <Image
                                src={createImageUrl(hit.image, {
                                  crop: "16:9",
                                })}
                                alt=""
                                width={48}
                                height={32}
                                className="object-cover shrink-0 rounded-sm"
                              />
                            ))}
                          <span className="font-medium text-gray-900">
                            {hit.title}
                          </span>
                        </a>
                      );
                    })}
                    <div className="border-b border-sand-500 mx-4" />
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <button
          onClick={handleClose}
          aria-label="Stäng sökning"
          className="h-[50px] w-[50px] shrink-0 flex items-center justify-center hover:bg-sand-700"
        >
          <Icon.Close className="h-4 fill-gray-500" />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={handleOpen}
      aria-label="Öppna sökning"
      className="h-[50px] w-[50px] flex items-center justify-center hover:bg-sand-700 border-l border-sand-800"
    >
      <Icon.Search className="h-7 w-7 text-gray-400" />
    </button>
  );
};

export default NavSearch;
